import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiX, FiEye, FiEyeOff, FiUser, FiLock, FiMail, FiAlertCircle, FiCheck } = FiIcons;

const AuthModal = ({ isOpen, onClose, mode = 'signin' }) => {
  const { signIn, signUp, resetPassword, loading } = useAuth();
  const [authMode, setAuthMode] = useState(mode);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const validateForm = () => {
    if (!formData.email) {
      setError('Email is required');
      return false;
    }
    if (!formData.email.includes('@')) {
      setError('Please enter a valid email address');
      return false;
    }
    if (authMode !== 'reset' && !formData.password) {
      setError('Password is required');
      return false;
    }
    if (authMode === 'signup') {
      if (formData.password.length < 6) {
        setError('Password must be at least 6 characters');
        return false;
      }
      if (formData.password !== formData.confirmPassword) {
        setError('Passwords do not match');
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setError('');
    setMessage('');

    try {
      if (authMode === 'signin') {
        const { error } = await signIn(formData.email, formData.password);
        if (error) {
          if (error.message.includes('Invalid login credentials')) {
            setError('Invalid email or password. Please check your credentials.');
          } else if (error.message.includes('Email not confirmed')) {
            setError('Please check your email and click the confirmation link before signing in.');
          } else {
            setError(error.message);
          }
        } else {
          setMessage('Successfully signed in!');
          setTimeout(onClose, 1000);
        }
      } else if (authMode === 'signup') {
        const { data, error } = await signUp(formData.email, formData.password);
        if (error) {
          if (error.message.includes('already registered')) {
            setError('An account with this email already exists. Please sign in instead.');
          } else {
            setError(error.message);
          }
        } else {
          if (data?.user && !data?.session) {
            setMessage('Please check your email and click the confirmation link to complete your registration.');
            setAuthMode('signin');
          } else {
            setMessage('Account created successfully!');
            setTimeout(onClose, 1000);
          }
        }
      } else if (authMode === 'reset') {
        const { error } = await resetPassword(formData.email);
        if (error) {
          setError(error.message);
        } else {
          setMessage('Password reset email sent! Please check your inbox.');
          setAuthMode('signin');
        }
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const switchMode = (newMode) => {
    setAuthMode(newMode);
    setError('');
    setMessage('');
    setFormData({
      email: formData.email,
      password: '',
      confirmPassword: ''
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50">
      <motion.div
        className="bg-gradient-to-br from-gray-900 to-gray-800 border border-gray-700 rounded-2xl w-full max-w-md shadow-2xl"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-700 flex items-center justify-between">
          <div className="flex items-center">
            <img 
              src="https://res.cloudinary.com/frontly/image/upload/v1756676497/Angry_Branding_Logo_ykea75.png" 
              alt="Angry Coder" 
              className="w-8 h-8 object-contain mr-3"
            />
            <h3 className="text-xl font-bold text-white">
              {authMode === 'signin' && 'Sign In'}
              {authMode === 'signup' && 'Create Account'}
              {authMode === 'reset' && 'Reset Password'}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <SafeIcon icon={FiX} className="text-xl" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Status Messages */}
          {error && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center">
              <SafeIcon icon={FiAlertCircle} className="text-red-400 mr-2 flex-shrink-0" />
              <span className="text-red-300 text-sm">{error}</span>
            </div>
          )}

          {message && (
            <div className="mb-4 p-3 bg-green-500/10 border border-green-500/30 rounded-lg flex items-center">
              <SafeIcon icon={FiCheck} className="text-green-400 mr-2 flex-shrink-0" />
              <span className="text-green-300 text-sm">{message}</span>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Email Address
              </label>
              <div className="relative">
                <SafeIcon icon={FiMail} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full pl-10 pr-4 py-3 bg-black border border-gray-700 rounded-lg focus:border-orange-500 focus:outline-none text-white"
                  placeholder="you@example.com"
                  required
                />
              </div>
            </div>

            {/* Password */}
            {authMode !== 'reset' && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Password
                </label>
                <div className="relative">
                  <SafeIcon icon={FiLock} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-12 py-3 bg-black border border-gray-700 rounded-lg focus:border-orange-500 focus:outline-none text-white"
                    placeholder="••••••••"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                  >
                    <SafeIcon icon={showPassword ? FiEyeOff : FiEye} />
                  </button>
                </div>
              </div>
            )}

            {/* Confirm Password */}
            {authMode === 'signup' && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Confirm Password
                </label>
                <div className="relative">
                  <SafeIcon icon={FiLock} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-12 py-3 bg-black border border-gray-700 rounded-lg focus:border-orange-500 focus:outline-none text-white"
                    placeholder="••••••••"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                  >
                    <SafeIcon icon={showConfirmPassword ? FiEyeOff : FiEye} />
                  </button>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting || loading}
              className="w-full bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 disabled:from-gray-700 disabled:to-gray-800 disabled:text-gray-500 px-6 py-3 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center"
            >
              {(isSubmitting || loading) ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
              ) : (
                <SafeIcon icon={FiUser} className="mr-2" />
              )}
              {authMode === 'signin' && 'Sign In'}
              {authMode === 'signup' && 'Create Account'}
              {authMode === 'reset' && 'Send Reset Email'}
            </button>
          </form>

          {/* Mode Switching */}
          <div className="mt-6 text-center space-y-2">
            {authMode === 'signin' && (
              <>
                <p className="text-gray-400 text-sm">
                  Don't have an account?{' '}
                  <button
                    onClick={() => switchMode('signup')}
                    className="text-orange-400 hover:text-orange-300 font-medium"
                  >
                    Sign up
                  </button>
                </p>
                <p className="text-gray-400 text-sm">
                  Forgot your password?{' '}
                  <button
                    onClick={() => switchMode('reset')}
                    className="text-orange-400 hover:text-orange-300 font-medium"
                  >
                    Reset it
                  </button>
                </p>
              </>
            )}
            {authMode === 'signup' && (
              <p className="text-gray-400 text-sm">
                Already have an account?{' '}
                <button
                  onClick={() => switchMode('signin')}
                  className="text-orange-400 hover:text-orange-300 font-medium"
                >
                  Sign in
                </button>
              </p>
            )}
            {authMode === 'reset' && (
              <p className="text-gray-400 text-sm">
                Remember your password?{' '}
                <button
                  onClick={() => switchMode('signin')}
                  className="text-orange-400 hover:text-orange-300 font-medium"
                >
                  Sign in
                </button>
              </p>
            )}
          </div>

          {/* Benefits */}
          <div className="mt-6 p-4 bg-orange-500/10 border border-orange-500/30 rounded-lg">
            <h4 className="text-orange-300 font-medium mb-2">🔥 Why create an account?</h4>
            <ul className="text-orange-200 text-sm space-y-1">
              <li>• Save your projects in the cloud</li>
              <li>• Access your code from any device</li>
              <li>• Never lose your work again</li>
              <li>• Sync settings across devices</li>
            </ul>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default AuthModal;